import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Mail, Lock, Loader2, User } from 'lucide-react';
import { Button } from '../components/Button';
import { Field, Input } from '../components/Input';
import { GoogleButton } from '../components/GoogleButton';
import { supabase } from '../supabaseClient';

/**
 * SignInPage — email + password sign in.
 * Sends a magic-link fallback option below the form for users who forget their password.
 */
export function SignInPage({ onAuthed }) {
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || '/';
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  async function submit(e) {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      const { data, error: e } = await supabase.auth.signInWithPassword({ email, password });
      if (e) throw e;
      onAuthed?.(data.user);
      navigate(from, { replace: true });
    } catch (e) {
      setError(e.message || 'Failed to sign in');
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthLayout title="Welcome back" subtitle="Sign in to your Datames account">
      <form onSubmit={submit} className="flex flex-col gap-4">
        <Field label="Email" htmlFor="email" required>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-fg-subtle pointer-events-none" />
            <Input
              id="email"
              type="email"
              required
              autoComplete="email"
              autoFocus
              placeholder="you@company.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="pl-9"
            />
          </div>
        </Field>
        <Field label="Password" htmlFor="password" required>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-fg-subtle pointer-events-none" />
            <Input
              id="password"
              type="password"
              required
              autoComplete="current-password"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="pl-9"
            />
          </div>
        </Field>

        {error && (
          <div className="px-3 py-2 rounded-md bg-destructive/10 border border-destructive/30 text-xs text-destructive">
            {error}
          </div>
        )}

        <Button
          type="submit"
          variant="primary"
          size="lg"
          disabled={busy}
          className="w-full"
        >
          {busy ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Signing in...
            </>
          ) : (
            'Sign in'
          )}
        </Button>

        {/* Divider + Google */}
        <div className="relative my-1">
          <div className="absolute inset-0 flex items-center" aria-hidden="true">
            <div className="w-full border-t border-border" />
          </div>
          <div className="relative flex justify-center">
            <span className="bg-bg px-3 text-2xs uppercase tracking-wider text-fg-subtle">
              or
            </span>
          </div>
        </div>

        <GoogleButton mode="signin" onAuthed={onAuthed} />

        <div className="flex items-center justify-between text-xs">
          <Link to="/forgot" className="text-fg-muted hover:text-brand transition-colors">
            Forgot password?
          </Link>
          <Link to="/signup" className="text-fg-muted hover:text-brand transition-colors">
            Don't have an account? <span className="font-medium">Sign up</span>
          </Link>
        </div>
      </form>

      <div className="mt-8 pt-6 border-t border-border">
        <p className="text-2xs text-fg-subtle text-center">
          By signing in, you agree to our Terms and Privacy Policy.
        </p>
      </div>
    </AuthLayout>
  );
}

/**
 * SignUpPage — create an account.
 * Triggers a free 50-credit grant via Supabase auth trigger.
 * (2026-07-12: was 100 credits — reduced to 50 for new signups.
 *  Existing accounts retain their original 100-credit grant.)
 */
export function SignUpPage({ onAuthed }) {
  const navigate = useNavigate();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const displayName = `${firstName.trim()} ${lastName.trim()}`.trim();

  async function submit(e) {
    e.preventDefault();
    if (!firstName.trim() || !lastName.trim()) {
      setError('Please enter your first and last name');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    setBusy(true); setError(null);
    try {
      // Persist first/last name + display_name in user_metadata.
      // The Supabase auth trigger reads this on the backend to set
      // the customers.display_name column.
      const { data, error: e } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: window.location.origin + '/',
          data: {
            first_name: firstName.trim(),
            last_name: lastName.trim(),
            display_name: displayName,
          },
        },
      });
      if (e) throw e;
      if (data.session) {
        onAuthed?.(data.user);
        navigate('/', { replace: true });
      } else {
        navigate('/check-email');
      }
    } catch (e) {
      setError(e.message || 'Failed to create account');
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthLayout title="Create your account" subtitle="50 free credits on signup. No card required.">
      <form onSubmit={submit} className="flex flex-col gap-4">
        {/* Name fields (required for display) */}
        <div className="grid grid-cols-2 gap-2">
          <Field label="First name" htmlFor="firstName" required>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-fg-subtle pointer-events-none" />
              <Input
                id="firstName"
                type="text"
                required
                autoComplete="given-name"
                autoFocus
                placeholder="Lezan"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className="pl-9"
              />
            </div>
          </Field>
          <Field label="Last name" htmlFor="lastName" required>
            <Input
              id="lastName"
              type="text"
              required
              autoComplete="family-name"
              placeholder="Fakhir"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
            />
          </Field>
        </div>

        <Field label="Email" htmlFor="email" required>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-fg-subtle pointer-events-none" />
            <Input
              id="email"
              type="email"
              required
              autoComplete="email"
              placeholder="you@company.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="pl-9"
            />
          </div>
        </Field>
        <Field
          label="Password"
          htmlFor="password"
          required
          description="At least 8 characters"
        >
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-fg-subtle pointer-events-none" />
            <Input
              id="password"
              type="password"
              required
              autoComplete="new-password"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="pl-9"
            />
          </div>
        </Field>

        {error && (
          <div className="px-3 py-2 rounded-md bg-destructive/10 border border-destructive/30 text-xs text-destructive">
            {error}
          </div>
        )}

        <Button
          type="submit"
          variant="primary"
          size="lg"
          disabled={busy}
          className="w-full"
        >
          {busy ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Creating account...
            </>
          ) : (
            'Create account'
          )}
        </Button>

        {/* Divider + Google */}
        <div className="relative my-1">
          <div className="absolute inset-0 flex items-center" aria-hidden="true">
            <div className="w-full border-t border-border" />
          </div>
          <div className="relative flex justify-center">
            <span className="bg-bg px-3 text-2xs uppercase tracking-wider text-fg-subtle">
              or
            </span>
          </div>
        </div>

        <GoogleButton mode="signup" onAuthed={onAuthed} />

        <div className="text-xs text-center">
          <Link to="/signin" className="text-fg-muted hover:text-brand transition-colors">
            Already have an account? <span className="font-medium">Sign in</span>
          </Link>
        </div>
      </form>
    </AuthLayout>
  );
}

/**
 * ForgotPasswordPage — email a magic link to reset.
 */
export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);

  async function submit(e) {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      const { error: e } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: window.location.origin + '/reset',
      });
      if (e) throw e;
      setDone(true);
    } catch (e) {
      setError(e.message || 'Failed to send reset email');
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthLayout title="Reset your password" subtitle="We'll email you a magic link.">
      {done ? (
        <div className="text-center py-4">
          <div className="mx-auto h-12 w-12 rounded-full bg-success/10 border border-success/30 flex items-center justify-center mb-3">
            <Mail className="h-5 w-5 text-success" />
          </div>
          <p className="text-sm text-fg">Check your inbox.</p>
          <p className="text-xs text-fg-muted mt-1">
            We sent a reset link to <span className="text-fg font-medium">{email}</span>.
          </p>
          <Link to="/signin">
            <Button variant="ghost" size="sm" className="mt-4">
              Back to sign in
            </Button>
          </Link>
        </div>
      ) : (
        <form onSubmit={submit} className="flex flex-col gap-4">
          <Field label="Email" htmlFor="email" required>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-fg-subtle pointer-events-none" />
              <Input
                id="email"
                type="email"
                required
                autoFocus
                placeholder="you@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="pl-9"
              />
            </div>
          </Field>

          {error && (
            <div className="px-3 py-2 rounded-md bg-destructive/10 border border-destructive/30 text-xs text-destructive">
              {error}
            </div>
          )}

          <Button type="submit" variant="primary" size="lg" disabled={busy} className="w-full">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Send reset link'}
          </Button>

          <div className="text-xs text-center">
            <Link to="/signin" className="text-fg-muted hover:text-brand transition-colors">
              Remember your password? <span className="font-medium">Sign in</span>
            </Link>
          </div>
        </form>
      )}
    </AuthLayout>
  );
}

/**
 * CheckEmailPage — confirmation after signup (when email confirmation is on).
 */
export function CheckEmailPage() {
  return (
    <AuthLayout title="Check your email" subtitle="We sent you a confirmation link.">
      <div className="text-center py-4">
        <div className="mx-auto h-12 w-12 rounded-full bg-brand/10 border border-brand/30 flex items-center justify-center mb-3">
          <Mail className="h-5 w-5 text-brand" />
        </div>
        <p className="text-sm text-fg">Click the link in the email to activate your account.</p>
        <p className="text-xs text-fg-muted mt-1">
          Didn't get it? Check your spam folder.
        </p>
        <Link to="/signin">
          <Button variant="ghost" size="sm" className="mt-4">
            Back to sign in
          </Button>
        </Link>
      </div>
    </AuthLayout>
  );
}

/**
 * AuthLayout — shared shell for sign in / sign up / forgot / check email.
 * Two-column on desktop (left: brand / right: form), single-column on mobile.
 */
function AuthLayout({ title, subtitle, children }) {
  return (
    <div className="min-h-screen flex flex-col lg:flex-row bg-bg">
      {/* Left brand panel — desktop only */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden border-r border-border">
        <div className="absolute inset-0 bg-gradient-to-br from-brand/8 via-transparent to-violet-500/8" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,hsl(var(--brand)/0.12),transparent_50%)]" />
        <div className="relative z-10 flex flex-col justify-between p-12">
          <div className="flex items-center gap-2.5">
            <img src="/logo.png" alt="Datames" className="h-9 w-9 rounded-md object-cover shadow-[0_0_0_1px_hsl(var(--border))]" draggable="false" />
            <div className="leading-tight">
              <div className="text-base font-semibold text-fg">Datames</div>
              <div className="text-2xs text-fg-muted uppercase tracking-wider">The all in one anti-bot API</div>
            </div>
          </div>

          <div className="space-y-6 max-w-md">
            <div>
              <div className="text-2xl font-semibold text-fg tracking-tight leading-tight">
                Scrape anything.
              </div>
              <div className="text-2xl font-semibold text-brand tracking-tight leading-tight">
                Without limits.
              </div>
            </div>
            <p className="text-sm text-fg-muted leading-relaxed">
              Cloudflare, Turnstile, hCaptcha — all solved in &lt;2 seconds.
              1 credit per call. No surprises.
            </p>
            <div className="flex items-center gap-6 pt-2">
              <Metric label="Avg solve" value="1.8s" />
              <Metric label="Success rate" value="99.4%" />
              <Metric label="Uptime" value="99.99%" />
            </div>
          </div>

          <div className="text-xs text-fg-subtle">
            Trusted by data teams at fast-growing startups.
          </div>
        </div>
      </div>

      {/* Right form panel */}
      <div className="flex-1 flex flex-col items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-sm">
          {/* Mobile brand */}
          <div className="lg:hidden flex items-center gap-2.5 mb-8">
            <img src="/logo.png" alt="Datames" className="h-8 w-8 rounded-md object-cover shadow-[0_0_0_1px_hsl(var(--border))]" draggable="false" />
            <span className="text-base font-semibold text-fg">Datames</span>
          </div>

          <div className="mb-8">
            <h1 className="text-2xl font-semibold text-fg tracking-tight">{title}</h1>
            {subtitle && <p className="text-sm text-fg-muted mt-1.5">{subtitle}</p>}
          </div>

          {children}
        </div>
      </div>
    </div>
  );
}

function Metric({ label, value }) {
  return (
    <div>
      <div className="text-lg font-semibold text-fg tabular-nums">{value}</div>
      <div className="text-2xs text-fg-muted uppercase tracking-wider">{label}</div>
    </div>
  );
}